import { useCallback, useEffect, useState } from 'react';
import { fetchRemotePurchasesSummary, fetchRemoteSupplierPerformance } from '../../infra/reportsRemotePurchasesRepo';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';
import type { PurchasesSummaryReport } from '../../domain/reportsPurchasesTypes';
import type { SupplierPerformanceRow } from '../../domain/reportsSupplierPerformanceTypes';
import { getReportsDateRange } from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';

type Params = {
  currentBranchId: string | null | undefined;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  shouldLoad: boolean;
};

export function useReportsComprasPane({
  currentBranchId,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  shouldLoad,
}: Params) {
  const [purchasesSummary, setPurchasesSummary] = useState<PurchasesSummaryReport | null>(null);
  const [supplierPerformance, setSupplierPerformance] = useState<SupplierPerformanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentBranchId || !shouldLoad) return;
    setLoading(true);
    setError(null);
    try {
      const { fromIso, toIso } = getReportsDateRange(new Date(), dateRangePreset, { from: customRangeFrom, to: customRangeTo });
      const online = await isOnline();
      let result: PurchasesSummaryReport | null = null;
      if (online) {
        result = await fetchRemotePurchasesSummary(currentBranchId, fromIso, toIso);
        const perf = await fetchRemoteSupplierPerformance(currentBranchId, fromIso, toIso);
        setSupplierPerformance(perf ?? []);
      } else {
        setSupplierPerformance([]);
      }
      setPurchasesSummary(result);
    } catch (err) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, dateRangePreset, customRangeFrom, customRangeTo, shouldLoad]);

  useEffect(() => {
    if (shouldLoad) load();
  }, [load, shouldLoad]);

  return {
    purchasesSummary,
    supplierPerformance,
    purchasesLoading: loading,
    purchasesError: error,
    reloadPurchases: load,
  };
}
